﻿import { toCyrillic, toLatin } from "serbian-transliteration";

declare global {
    interface Window {
        transliterate: (toCyrillicFlag?: boolean) => void;
        gtag?: (command: string, action: string, parameters?: any) => void;
        google?: {
            translate: {
                TranslateElement: new (elementId: string) => any;
            };
        };
        getElementHeight: (element: Element) => number | null;
        getElementDistanceToTop: (element: Element) => number | null;
        getElementDistanceToBottom: (element: Element) => number | null;
    }
}

const STORAGE_KEY = 'cyrillicMode';

function getCyrillicModeFromStorage(): boolean {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null ? JSON.parse(stored) : true; // Default to true if not set
}

function setCyrillicModeToStorage(value: boolean): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

let isCyrillicMode: boolean = getCyrillicModeFromStorage();

function normalizeDisplayedWebUrl(value: string): string | null {
    let candidate = value.trim().replace(/\s+/g, '');
    if (!candidate) return null;

    if (candidate.startsWith('//')) {
        candidate = `https:${candidate}`;
    } else if (!/^[a-z][a-z\d+.-]*:\/\//i.test(candidate)) {
        if (!/^(?:www\.)?[^/?#]+\.[^/?#]+(?:[/?#]|$)/i.test(candidate)) return null;
        candidate = `https://${candidate}`;
    }

    try {
        const url = new URL(candidate);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

        const hostname = url.hostname.toLocaleLowerCase().replace(/^www\./i, '');
        const pathname = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
        return `${hostname}${url.port ? `:${url.port}` : ''}${pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

function displaysItsDestination(anchor: HTMLAnchorElement): boolean {
    const text = anchor.textContent?.trim().replace(/\s+/g, '') ?? '';
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (!text || !href) return false;

    if (text === href) return true;

    if (/^mailto:/i.test(href)) {
        return text === href.substring(href.indexOf(':') + 1);
    }

    if (/^tel:/i.test(href)) {
        const normalizePhoneNumber = (value: string): string => value.replace(/[^\d+]/g, '');
        return normalizePhoneNumber(text) === normalizePhoneNumber(href.substring(href.indexOf(':') + 1));
    }

    const displayedUrl = normalizeDisplayedWebUrl(text);
    const destinationUrl = normalizeDisplayedWebUrl(href);
    return displayedUrl !== null && displayedUrl === destinationUrl;
}

function shouldTransliterateTextNode(node: Node): boolean {
    const parent = node.parentElement;
    if (!parent || parent.closest('[data-no-transliterate], #cconsent-modal, #cconsent-bar')) return false;

    const anchor = parent.closest('a');
    return !(anchor instanceof HTMLAnchorElement && displaysItsDestination(anchor));
}

export function transliterate(): void {
    const toCyrillicFlag: boolean = getCyrillicModeFromStorage();
    const elements: HTMLElement[] = [
        document.getElementsByClassName("main")[0],
        document.getElementsByClassName("ccb__wrapper")[0],
        document.getElementsByClassName("ccm__content")[0]
    ].filter((el): el is HTMLElement => el instanceof HTMLElement);

    elements.forEach((element: HTMLElement) => {
        const walker: TreeWalker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

        let node: Node | null;
        while ((node = walker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE && node.nodeValue && shouldTransliterateTextNode(node)) {
                const text: string = node.nodeValue.trim();
                if (!text) continue;

                node.nodeValue = toCyrillicFlag
                    ? toCyrillic(node.nodeValue)
                    : toLatin(node.nodeValue);
            }
        }
    });
}

function toggleTransliteration(): void {
    isCyrillicMode = !isCyrillicMode;
    setCyrillicModeToStorage(isCyrillicMode); // Save to localStorage
    transliterate();
    updateToggleButton();
}

function updateToggleButton(): void {
    const toggleButton = document.getElementById('transliterate-toggle') as HTMLButtonElement;
    const toggleText = toggleButton?.querySelector('.transliterate-text') as HTMLSpanElement;

    if (toggleButton && toggleText) {
        if (isCyrillicMode) {
            toggleText.textContent = 'Latinica';
            toggleButton.setAttribute('aria-pressed', 'true');
            toggleButton.classList.add('active');
        } else {
            toggleText.textContent = 'Ћирилица';
            toggleButton.setAttribute('aria-pressed', 'false');
            toggleButton.classList.remove('active');
        }
    }
}

function initializeTransliterationToggle(): void {
    const toggleButton = document.getElementById('transliterate-toggle') as HTMLButtonElement;

    if (toggleButton) {
        toggleButton.setAttribute('aria-pressed', 'false');
        toggleButton.setAttribute('aria-label', 'Toggle between Latin and Cyrillic script');

        toggleButton.addEventListener('click', toggleTransliteration);

        updateToggleButton();
    }
}

function normalizeServiceFinderText(value: string): string {
    return toLatin(value).toLocaleLowerCase().trim();
}

function initializeServiceFinders(): void {
    document.querySelectorAll<HTMLElement>('[data-service-finder]').forEach((finder: HTMLElement): void => {
        const searchInput = finder.querySelector<HTMLInputElement>('[data-service-search]');
        const categorySelect = finder.querySelector<HTMLElement>('[data-service-category-select]');
        const categoryToggle = finder.querySelector<HTMLButtonElement>('[data-service-category-toggle]');
        const categoryMenu = finder.querySelector<HTMLElement>('[data-service-category-menu]');
        const categoryDisplay = finder.querySelector<HTMLElement>('[data-service-category-display]');
        const categoryOptions = Array.from(finder.querySelectorAll<HTMLElement>('[data-service-category-option]'));
        const characterButtons = Array.from(finder.querySelectorAll<HTMLButtonElement>('[data-service-character]'));
        const serviceCards = Array.from(finder.querySelectorAll<HTMLElement>('[data-service-card]'));
        const emptyMessage = finder.querySelector<HTMLElement>('[data-service-empty]');
        const maxResults = Number.parseInt(finder.dataset.maxResults ?? '', 10) || 50;

        if (!searchInput || !categorySelect || !categoryToggle || !categoryMenu || !categoryDisplay) {
            return;
        }

        let selectedCharacter = '';
        let selectedCategory = categorySelect.dataset.selectedCategory ?? '';
        let closeCategoryTimeout: number | undefined;

        const setCategoryOpen = (isOpen: boolean): void => {
            if (closeCategoryTimeout !== undefined) {
                window.clearTimeout(closeCategoryTimeout);
                closeCategoryTimeout = undefined;
            }

            if (isOpen) {
                categoryMenu.hidden = false;
                categoryToggle.setAttribute('aria-expanded', 'true');
                categoryMenu.setAttribute('aria-expanded', 'true');
                (categoryOptions.find(option => option.dataset.serviceCategoryOption === selectedCategory) ?? categoryOptions[0])?.focus();
                return;
            }

            categoryToggle.setAttribute('aria-expanded', 'false');
            categoryMenu.setAttribute('aria-expanded', 'false');
            closeCategoryTimeout = window.setTimeout((): void => {
                if (categoryMenu.getAttribute('aria-expanded') === 'false') {
                    categoryMenu.hidden = true;
                }
            }, 150);
        };

        const filterServices = (): void => {
            const search = normalizeServiceFinderText(searchInput.value);
            let matchingCount = 0;

            serviceCards.forEach((serviceCard: HTMLElement): void => {
                const matchesSearch = !search || normalizeServiceFinderText(serviceCard.dataset.serviceName ?? '').includes(search);
                const matchesCategory = !selectedCategory || serviceCard.dataset.serviceCategory === selectedCategory;
                const matchesCharacter = !selectedCharacter || serviceCard.dataset.serviceCharacter === selectedCharacter;
                const matches = matchesSearch && matchesCategory && matchesCharacter;

                serviceCard.hidden = !matches || matchingCount >= maxResults;
                if (matches) {
                    matchingCount++;
                }
            });

            if (emptyMessage) {
                emptyMessage.hidden = matchingCount !== 0;
            }
        };

        searchInput.addEventListener('input', filterServices);

        categoryToggle.addEventListener('click', (): void => {
            setCategoryOpen(categoryToggle.getAttribute('aria-expanded') !== 'true');
        });

        categoryOptions.forEach((option: HTMLElement): void => {
            option.addEventListener('click', (): void => {
                selectedCategory = option.dataset.serviceCategoryOption ?? '';
                categoryDisplay.textContent = selectedCategory ? option.textContent?.trim() ?? '' : '';

                categoryOptions.forEach((categoryOption: HTMLElement): void => {
                    categoryOption.setAttribute('aria-selected', (categoryOption === option).toString());
                });

                setCategoryOpen(false);
                categoryToggle.focus();
                filterServices();
            });
        });

        categorySelect.addEventListener('keydown', (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setCategoryOpen(false);
                categoryToggle.focus();
                return;
            }

            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') {
                return;
            }

            event.preventDefault();
            if (categoryToggle.getAttribute('aria-expanded') !== 'true') {
                setCategoryOpen(true);
                return;
            }

            const focusedOptionIndex = categoryOptions.indexOf(document.activeElement as HTMLElement);
            if (event.key === 'Enter' && focusedOptionIndex >= 0) {
                categoryOptions[focusedOptionIndex].click();
                return;
            }

            const offset = event.key === 'ArrowUp' ? -1 : 1;
            const nextOptionIndex = Math.min(Math.max(focusedOptionIndex + offset, 0), categoryOptions.length - 1);
            categoryOptions[nextOptionIndex]?.focus();
        });

        categorySelect.addEventListener('focusout', (event: FocusEvent): void => {
            if (!(event.relatedTarget instanceof Node) || !categorySelect.contains(event.relatedTarget)) {
                setCategoryOpen(false);
            }
        });

        characterButtons.forEach((button: HTMLButtonElement): void => {
            button.addEventListener('click', (): void => {
                const character = button.dataset.serviceCharacter ?? '';
                selectedCharacter = selectedCharacter === character ? '' : character;

                characterButtons.forEach((characterButton: HTMLButtonElement): void => {
                    const isActive = characterButton.dataset.serviceCharacter === selectedCharacter;
                    characterButton.parentElement?.classList.toggle('active', isActive);
                    characterButton.setAttribute('aria-pressed', isActive.toString());
                });

                filterServices();
            });
        });

        filterServices();
    });
}

window.transliterate = transliterate;

function onTranslateEvent(lang: string): void {
    if (window.gtag) {
        window.gtag('event', 'translate', {
            event_category: 'Localization',
            event_label: lang // e.g. "fr", "de"
        });
    }
}

function googleTranslateElementInit(): void {
    if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement('google_translate_element');
        const select = document.querySelector('#google_translate_element select') as HTMLSelectElement;
        select?.addEventListener('change', (e: Event) => {
            const target = e.target as HTMLSelectElement;
            onTranslateEvent(target.value);
        });
    }
}

document.addEventListener('DOMContentLoaded', (): void => {
    initializeTransliterationToggle();
    initializeServiceFinders();

    document.querySelectorAll('.accordion').forEach((accordion: Element) => {
        const toggleButton = accordion.querySelector('.accordion-toggle') as HTMLButtonElement;
        const content = accordion.querySelector('.accordion-content') as HTMLDivElement;
        const icon = accordion.querySelector('.accordion-icon') as SVGElement;

        // Check if all required elements exist before adding event listeners
        if (!toggleButton || !content || !icon) {
            return;
        }

        let isOpen: boolean = false;

        toggleButton.addEventListener('click', (): void => {
            isOpen = !isOpen;

            if (isOpen) {
                content.style.maxHeight = content.scrollHeight + 'px';
                icon.classList.add('rotate');
            } else {
                content.style.maxHeight = '0';
                icon.classList.remove('rotate');
            }
        });
    });
});

window.getElementHeight = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    const htmlElement = element as HTMLElement;
    const offsetHeight: number = htmlElement.offsetHeight;

    if (!offsetHeight || offsetHeight > (window.innerHeight - element.getBoundingClientRect().top)) {
        return Math.floor(window.innerHeight - element.getBoundingClientRect().top);
    }

    return Math.floor(offsetHeight);
};

window.getElementDistanceToTop = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    return Math.floor(element.getBoundingClientRect().y);
};

window.getElementDistanceToBottom = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    return Math.floor(window.innerHeight - element.getBoundingClientRect().y);
};
